import { Outlet, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { MyPageSidebar } from './MyPageSidebar';
import { ROUTE_CONFIGS, getWrapClass } from '@configs/routes';

export type MyPageLayoutOutletContext = {
  setMyPageFooter: (footer: ReactNode | null) => void;
};

type MyPageRouteHandle = {
  menuTitle?: string;
  title?: string;
  description?: string;
};

type RouteEntry = {
  fullPath: string;
  title?: string;
  startsWithPaths?: string[];
  description?: string;
};

type CssRuleMap = Record<string, CSSProperties>;
type CssSheet = {
  rules?: CssRuleMap;
  media?: Record<string, CssRuleMap>;
};

const toKebabCase = (value: string) => value.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

const serializeDeclarations = (style: CSSProperties) => {
  return Object.entries(style)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([prop, raw]) => {
      const key = toKebabCase(prop);
      const val = typeof raw === 'number' ? String(raw) : String(raw);
      return `${key}:${val};`;
    })
    .join('');
};

const serializeRules = (rules: CssRuleMap) => {
  return Object.entries(rules)
    .map(([selector, style]) => {
      const decls = serializeDeclarations(style);
      return decls ? `${selector}{${decls}}` : '';
    })
    .filter(Boolean)
    .join('');
};

const serializeSheet = (sheet: CssSheet) => {
  const base = sheet.rules ? serializeRules(sheet.rules) : '';
  const media = sheet.media
    ? Object.entries(sheet.media)
        .map(([query, rules]) => {
          const body = serializeRules(rules);
          return body ? `${query}{${body}}` : '';
        })
        .filter(Boolean)
        .join('')
    : '';
  return `${base}${media}`;
};

const hasDescription = (route: unknown): route is { description?: string } => {
  return typeof route === 'object' && route !== null && 'description' in route;
};

const isRouteEntry = (route: unknown): route is RouteEntry => {
  if (typeof route !== 'object' || route === null) return false;
  if (!('fullPath' in route)) return false;
  return typeof (route as { fullPath?: unknown }).fullPath === 'string';
};

export const MyPageLayout = () => {
  const location = useLocation();
  const wrapClass = getWrapClass(location.pathname);

  const isMyPagePaymentWrap = useMemo(() => wrapClass.split(' ').includes('mypagePayment_wrap'), [wrapClass]);

  const [isMobileViewport, setIsMobileViewport] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1080px)');
    const handleChange = () => setIsMobileViewport(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [lockedBody, setLockedBody] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [footer, setFooter] = useState<ReactNode | null>(null);

  const setMyPageFooter = useCallback((nextFooter: ReactNode | null) => {
    setFooter(nextFooter);
  }, []);

  const outletContext = useMemo<MyPageLayoutOutletContext>(() => ({ setMyPageFooter }), [setMyPageFooter]);

  const pageMeta = useMemo<MyPageRouteHandle>(() => {
    const pathname = location.pathname;

    // 상세 경로는 startsWithPaths로는 제목이 부모로 고정되므로, 여기서 별도로 제목 지정
    if (pathname.startsWith(`${ROUTE_CONFIGS.MY_PAGE.routes.ORDER_HISTORY.fullPath}/`)) {
      return {
        menuTitle: ROUTE_CONFIGS.MY_PAGE.routes.ORDER_HISTORY.title,
        title: '주문 상세',
        description: '주문한 상품의 상세 정보를 확인하세요',
      };
    }

    if (pathname.startsWith(`${ROUTE_CONFIGS.MY_PAGE.routes.REFUND_HISTORY.fullPath}/`)) {
      return {
        menuTitle: ROUTE_CONFIGS.MY_PAGE.routes.REFUND_HISTORY.title,
        title: '환불 상세',
      };
    }

    const routeGroups: Array<Record<string, unknown>> = [
      ROUTE_CONFIGS.MY_PAGE.routes as unknown as Record<string, unknown>,
      ROUTE_CONFIGS.SELLER_MY_PAGE.routes as unknown as Record<string, unknown>,
    ];

    for (const group of routeGroups) {
      for (const route of Object.values(group)) {
        if (!isRouteEntry(route)) continue;
        if (route.fullPath === pathname) {
          const description = hasDescription(route) ? route.description : undefined;
          return { menuTitle: route.title, title: route.title, description };
        }

        // 상세 페이지/하위 경로는 startsWithPaths가 있으면 해당 기준을 사용
        if (Array.isArray(route.startsWithPaths) && route.startsWithPaths.some((p) => pathname.startsWith(p))) {
          const description = hasDescription(route) ? route.description : undefined;
          return { menuTitle: route.title, title: route.title, description };
        }
      }
    }

    return {};
  }, [location.pathname]);

  // 모바일(작은 뷰포트)에서 메뉴 버튼 클릭 시 사이드바 노출
  useEffect(() => {
    const handleMenuButtonClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.mypage_wrap .breadcrumb .menu_btn')) {
        const isMobileViewport = window.matchMedia('(max-width: 1080px)').matches;
        if (!isMobileViewport) return;
        event.preventDefault();
        setIsMenuOpen(true);
      }
    };

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      const clickedMenuButton = target instanceof HTMLElement && target.closest('.mypage_wrap .breadcrumb .menu_btn');
      const clickedInsideMenu = menuRef.current?.contains(target as Node) ?? false;

      if (!clickedMenuButton && !clickedInsideMenu) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleMenuButtonClick);
    document.addEventListener('mouseup', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleMenuButtonClick);
      document.removeEventListener('mouseup', handleOutsideClick);
    };
  }, []);

  // 메뉴 열림/닫힘에 따라 스크롤 제어
  useEffect(() => {
    if (isMenuOpen) {
      if (!document.body.classList.contains('hidden')) {
        document.body.classList.add('hidden');
        setLockedBody(true);
      }
    } else {
      if (lockedBody) {
        document.body.classList.remove('hidden');
        setLockedBody(false);
      }
    }

    return () => {
      if (lockedBody) {
        document.body.classList.remove('hidden');
      }
    };
  }, [isMenuOpen, lockedBody]);

  // 페이지 이동 시 메뉴 닫기
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const sectionStyle = useMemo<CSSProperties>(
    () => ({ paddingBottom: isMobileViewport ? '8rem' : '6.25rem' }),
    [isMobileViewport],
  );

  const contentRowStyle = useMemo<CSSProperties>(
    () => ({
      display: 'flex',
      gap: isMobileViewport ? '1rem' : '1.25rem',
      flexDirection: isMobileViewport ? 'column' : 'row',
    }),
    [isMobileViewport],
  );

  const myContentStyle = useMemo<CSSProperties>(
    () => ({
      width: isMobileViewport ? '100%' : 'calc(100% - 11.25rem - 1.25rem)',
      marginTop: isMobileViewport ? 0 : '2.5rem',
      border: isMobileViewport ? 0 : '1px solid #acacc5',
      borderRadius: isMobileViewport ? 0 : '0.625rem',
      background: '#fff',
      padding: '2rem 3rem',
    }),
    [isMobileViewport],
  );

  const titleBoxStyle = useMemo<CSSProperties>(
    () => ({
      display: 'flex',
      alignItems: isMobileViewport ? 'baseline' : 'baseline',
      flexDirection: isMobileViewport ? 'column' : 'row',
      gap: isMobileViewport ? '1.2rem' : '1.25rem',
      marginBottom: isMyPagePaymentWrap ? '2.125rem' : isMobileViewport ? '2.4rem' : '1.75rem',
    }),
    [isMobileViewport, isMyPagePaymentWrap],
  );

  const breadcrumbStyle = useMemo<CSSProperties>(
    () => ({ display: isMobileViewport ? 'flex' : 'none' }),
    [isMobileViewport],
  );

  const titleStyle = useMemo<CSSProperties>(
    () => ({
      fontWeight: 600,
      fontSize: isMobileViewport ? '1.8rem' : '1.875rem',
      lineHeight: '100%',
      textTransform: 'uppercase',
      color: '#000',
      margin: 0,
    }),
    [isMobileViewport],
  );

  const descStyle = useMemo<CSSProperties>(
    () => ({
      fontWeight: 400,
      fontSize: isMobileViewport ? '1.2rem' : '0.875rem',
      lineHeight: isMobileViewport ? '140%' : '100%',
      textTransform: 'uppercase',
      color: '#666',
      margin: 0,
      wordBreak: isMobileViewport ? 'keep-all' : undefined,
    }),
    [isMobileViewport],
  );

  const contBoxStyle = useMemo<CSSProperties>(() => ({ position: 'relative', width: '100%' }), []);

  const myPageCommonCss = useMemo(() => {
    const rechargeCss = wrapClass.split(' ').includes('mypageRecharge_wrap')
      ? serializeSheet({
          rules: {
            '.mypageRecharge_wrap .my_content .my_contitem1': {
              marginBottom: '5.938rem',
            },
            '.mypageRecharge_wrap .my_content .my_contitem1 .notebox': {
              marginBottom: '1.25rem',
            },
            '.mypageRecharge_wrap .my_content .my_contitem1 .form_itembox': {
              marginBottom: '2.188rem',
              gap: '0.938rem',
            },
            '.mypageRecharge_wrap .my_content .my_contitem1 .form_itembox .input_box .label_text': {
              width: '10.313rem',
            },
            '.mypageRecharge_wrap .my_content .my_contitem1 .infodesc': {
              fontWeight: 400,
              fontSize: '0.875rem',
              lineHeight: '143%',
              textTransform: 'uppercase',
              color: '#000',
            },
            '.mypageRecharge_wrap .my_content .my_contitem1 .infodesc .bold': {
              fontWeight: 600,
            },
            '.mypageRecharge_wrap .my_content .my_contitem2 .tbl_top': {
              marginBottom: '1.625rem',
            },
            '.mypageRecharge_wrap .my_content .my_contitem2 .my_tbl thead tr th': {
              textAlign: 'left',
              paddingLeft: '1.25rem',
            },
            '.mypageRecharge_wrap .my_content .my_contitem2 .my_tbl thead tr th:last-child': {
              paddingRight: '1.875rem',
            },
            '.mypageRecharge_wrap .my_content .my_contitem2 .my_tbl thead tr th.padding1': {
              paddingLeft: '3.125rem',
            },
            '.mypageRecharge_wrap .my_content .my_contitem2 .my_tbl tbody tr td:last-child': {
              paddingRight: '1.875rem',
            },
            '.mypageRecharge_wrap .my_content .my_contitem2 .pagination': {
              marginTop: '1.563rem',
              marginBottom: '4.375rem',
            },
          },
          media: {
            '@media screen and (max-width: 1080px)': {
              '.mypageRecharge_wrap .my_content .my_contitem1 .form_itembox': {
                marginBottom: '2rem',
                gap: '1.2rem',
              },
              '.mypageRecharge_wrap .my_content .my_contitem2 .tbl_top': {
                marginBottom: '1.4rem',
              },
              '.mypageRecharge_wrap .my_content .my_contitem2 .pagination': {
                marginTop: '2rem',
                marginBottom: 0,
              },
            },
          },
        })
      : '';

    const purchaseCss = wrapClass.split(' ').includes('mypagePurchase_wrap')
      ? serializeSheet({
          rules: {
            '.mypagePurchase_wrap .my_contbox .prdlist .prditem > .prdbox .txtbox .title': {
              marginBottom: '0.875rem',
              fontSize: '1.375rem',
              fontWeight: 600,
            },
            '.mypagePurchase_wrap .my_contbox .prdlist .prditem > .prdbox .txtbox .info': {
              fontSize: '1.125rem',
              lineHeight: '100%',
              color: '#000',
              marginBottom: '0.875rem',
            },
            '.mypagePurchase_wrap .my_contbox .prdlist .prditem > .prdbox .txtbox .info .price': {
              fontWeight: 600,
            },
            '.mypagePurchase_wrap .my_contbox .prdlist .prditem > .prdbox .txtbox': {
              paddingTop: '0.375rem',
            },
          },
        })
      : '';

    const sellerCss = wrapClass.split(' ').includes('mypageSeller_wrap')
      ? serializeSheet({
          rules: {
            '.mypageSeller_wrap .my_content .my_contitem2 .form_itembox': {
              gap: '2.875rem',
            },
            '.mypageSeller_wrap .my_content .my_contitem2 .form_item': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '0.75rem',
            },
            '.mypageSeller_wrap .my_content .my_contitem2 .form_item .input_box': {
              width: '100%',
            },
            '.mypageSeller_wrap .my_content .my_contitem2 .form_item .input_box .label_text': {
              width: '9.375rem',
              fontSize: '1rem',
            },
            '.mypageSeller_wrap .my_content .my_contitem2 .form_item .input_box .input_text:not(textarea)': {
              height: '2.625rem',
            },
            '.mypageSeller_wrap .my_content .my_contitem2 .form_item .input_box .input_text::placeholder': {
              fontWeight: 500,
              color: '#999',
            },
            '.mypageSeller_wrap .my_content .my_contitem2 .form_item .input_box .select': {
              height: '2.625rem',
            },
            '.mypageSeller_wrap .my_content .my_contitem2 .form_item .input_box .file_box .label_file': {
              height: '2.625rem',
            },
            '.mypageSeller_wrap .my_content .my_contitem2 .form_item .input_box .input_flex': {
              gap: '0.375rem',
            },
            '.mypageSeller_wrap .my_content .my_contitem2 .form_item .input_box .input_flex.w448': {
              maxWidth: '28rem',
            },
            '.mypageSeller_wrap .my_content .my_contitem2 .form_item .input_box .input_flex .button': {
              fontWeight: 400,
              fontSize: '1rem',
              padding: '0 1.5rem',
              height: '2.625rem',
            },
            '.mypageSeller_wrap .my_content .my_contitem2 .form_item .input_desc': {
              paddingLeft: '10.313rem',
            },
          },
        })
      : '';

    const memberCss = wrapClass.split(' ').includes('mypageMember_wrap')
      ? serializeSheet({
          rules: {
            '.mypageMember_wrap .my_content .my_titbox': {
              marginBottom: '1.125rem !important',
            },
            '.mypageMember_wrap .my_content .my_contitem1': {
              marginBottom: '2.688rem',
            },
            '.mypageMember_wrap .my_content .my_contitem2 .form_box + .form_box': {
              marginTop: '3.75rem',
            },
            '.mypageMember_wrap .my_content .my_contitem2 .form_titbox': {
              fontWeight: 500,
              fontSize: '1.125rem',
              lineHeight: '100%',
              textTransform: 'uppercase',
              color: '#000',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.563rem',
            },
            '.mypageMember_wrap .my_content .my_contitem2 .form_titbox .icon': {
              width: '1.25rem',
            },
            '.mypageMember_wrap .my_content .my_contitem2 .form_itembox': {
              gap: '0.75rem',
            },
            '.mypageMember_wrap .my_content .my_contitem2 .form_item .input_box .label_text': {
              width: '9.375rem',
              fontSize: '1rem',
              padding: 0,
              alignSelf: 'center',
            },
            '.mypageMember_wrap .my_content .my_contitem2 .form_item .input_box .input_text': {
              height: '2.625rem',
            },
            '.mypageMember_wrap .my_content .my_contitem2 .form_item .input_box .select': {
              height: '2.625rem',
            },
            '.mypageMember_wrap .my_content .my_contitem2 .form_btnbox': {
              marginTop: '3.125rem',
              padding: '3.125rem 0 3.5rem',
              borderTop: '1px solid #acacc5',
            },
          },
        })
      : '';

    const productCss = wrapClass.split(' ').includes('mypageProduct_wrap')
      ? serializeSheet({
          rules: {
            '.mypageProduct_wrap .my_content .my_contitem2 .prdtop': {
              marginBottom: '1.063rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem2 .prdtop .rightbox': {
              gap: '0.563rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem2 .prdtop .rightbox .select_box .select': {
              height: '2.125rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem2 .prdtop .rightbox .prdadd_btn': {
              height: '2.125rem',
              padding: '0 1.688rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem2 .prdlist .prditem .btnbox': {
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              alignSelf: 'center',
            },
            '.mypageProduct_wrap .my_content .my_contitem2 .prdlist .prditem .btnbox .prd_btn': {
              height: 'auto',
              padding: '0.938rem',
              gap: '0.5rem',
              fontSize: '1rem',
              borderRadius: '0.313rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem2 .prdlist .prditem .btnbox .prd_btn .icon': {
              width: '1.25rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem3 .form_title': {
              fontSize: '1.5rem',
              fontWeight: 600,
              lineHeight: '100%',
              marginBottom: '2.5rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem3 .form_titbox': {
              fontWeight: 500,
              fontSize: '1.125rem',
              lineHeight: '100%',
              textTransform: 'uppercase',
              color: '#000',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.563rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem3 .form_titbox .icon': {
              width: '1.25rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem3 .form_itembox': {
              gap: '1.25rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem3 .form_item.flex': {
              display: 'flex',
              gap: '2.5rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem3 .form_item.flex .input_box': {
              flex: 1,
            },
            '.mypageProduct_wrap .my_content .my_contitem3 .form_item .input_box .label_text': {
              width: '9.375rem',
              fontSize: '1rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem3 .form_item .input_box .input_text:not(textarea)': {
              height: '2.625rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem3 .form_item .input_box textarea.input_text': {
              height: '15rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem3 .form_item .input_box .select': {
              height: '2.625rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem3 .form_item .input_wrap .input_desc': {
              paddingLeft: '10.313rem',
            },
            '.mypageProduct_wrap .my_content .my_contitem3 .form_btnbox': {
              marginTop: '3.125rem',
              padding: '3.125rem 0 3.5rem',
              borderTop: '1px solid #acacc5',
              gap: '0.688rem',
            },
          },
        })
      : '';

    const sellerRefundCss = wrapClass.split(' ').includes('mypageSellerRefund_wrap')
      ? serializeSheet({
          rules: {
            '.mypageSellerRefund_wrap .my_content .my_contitem2 .prdtop': {
              marginBottom: '1.063rem',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem2 .prdlist .prditem .totalbox': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              alignSelf: 'center',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem2 .prdlist .prditem .totalbox .tit': {
              fontSize: '1.125rem',
              lineHeight: '100%',
              color: '#000',
              marginBottom: '0.5rem',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem2 .prdlist .prditem .totalbox .price': {
              fontSize: '2rem',
              lineHeight: '100%',
              fontWeight: 600,
              color: '#d80b0b',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem2 .prdlist .prditem .totalbox .button': {
              height: '2.5rem',
              fontSize: '1rem',
              width: 'auto',
              marginTop: '1.125rem',
            },

            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_top': {
              display: 'flex',
              alignItems: 'center',
              gap: '1.375rem',
              padding: '1.563rem 2.5rem',
              marginBottom: '1.563rem',
              borderBottom: '1px solid #acacc5',
              background: '#fff',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_top .imgbox': {
              position: 'relative',
              width: '5rem',
              aspectRatio: '1 / 1',
              overflow: 'hidden',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_top .imgbox .thum': {
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_top .txtbox': {
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '2rem',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_top .txtbox .leftbox': {
              flex: 1,
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_top .txtbox .rightbox': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: '0.75rem',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_top .txtbox .tit': {
              fontSize: '1.375rem',
              fontWeight: 600,
              color: '#000',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_top .txtbox .price': {
              fontSize: '2rem',
              lineHeight: '100%',
              fontWeight: 600,
              color: '#d80b0b',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_top .txtbox .desc': {
              fontSize: '1.25rem',
              lineHeight: '100%',
              color: '#000',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_top .txtbox .desc .color': {
              fontWeight: 600,
            },

            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont': {
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid #acacc5',
              borderRadius: '0.625rem',
              background: '#fff',
              overflow: 'hidden',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .topbox': {
              padding: '1.25rem',
              background: '#f1f5f9',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .topbox .tag': {
              color: '#d80b0b',
              background: '#ffeded',
              fontSize: '1rem',
              fontWeight: 600,
              padding: '0.5rem 1rem',
              borderRadius: '6.25rem',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .topbox .info': {
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .topbox .info .txt': {
              fontSize: '1.125rem',
              fontWeight: 500,
              color: '#626281',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .topbox .info .bar': {
              width: '1px',
              height: '0.875rem',
              background: '#626281',
              opacity: 0.5,
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .bottombox': {
              padding: '2.5rem 1.5rem',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .bottombox .infobox': {
              display: 'flex',
              flexDirection: 'column',
              gap: '2.5rem',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .bottombox .infobox .item': {
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .bottombox .infobox .item .title': {
              fontSize: '1.125rem',
              fontWeight: 500,
              color: '#000000',
              lineHeight: '100%',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .bottombox .infobox .item .descbox': {
              padding: '1.25rem 2rem',
              borderRadius: '0.625rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '1rem',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .bottombox .infobox .item .descitem': {
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              fontSize: '1.125rem',
              lineHeight: '100%',
              color: '#000',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .bottombox .infobox .item .descitem .tit':
              {
                fontWeight: 600,
              },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .bottombox .infobox .item1 .descbox': {
              border: '1px solid rgba(216, 11, 11, 0.8)',
              background: 'rgba(216, 11, 11, 0.05)',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .bottombox .infobox .item2 .descbox': {
              border: '1px solid rgba(73, 73, 233, 0.8)',
              background: 'rgba(73, 73, 233, 0.05)',
            },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .bottombox .infobox .item1 .descitem .tit':
              {
                color: '#d80b0b',
              },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .bottombox .infobox .item2 .descitem .tit':
              {
                color: '#4949e9',
                width: '4rem',
              },
            '.mypageSellerRefund_wrap .my_content .my_contitem3 .detail_cont .bottombox .count': {
              textAlign: 'right',
              marginTop: '1.5rem',
              fontSize: '1rem',
            },
          },
        })
      : '';

    const baseCss = serializeSheet({
      rules: {
        '.mypage_wrap .my_titbox': {
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'baseline',
          gap: '1.25rem',
        },
        '.mypage_wrap .my_titbox .breadcrumb': {
          display: 'none',
        },
        '.mypage_wrap .my_titbox .tit': {
          fontWeight: 600,
          fontSize: '1.875rem',
          lineHeight: '100%',
          textTransform: 'uppercase',
          color: '#000',
          margin: 0,
        },
        '.mypage_wrap .my_titbox .desc': {
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: '100%',
          textTransform: 'uppercase',
          color: '#666',
          margin: 0,
        },

        '.mypage_wrap .my_contbox .notebox': {
          display: 'flex',
          flexDirection: 'column',
          gap: '0.625rem',
          padding: '1rem 1.25rem !important',
          border: '1px solid #acacc5 !important',
          borderRadius: '0.313rem',
          background: '#f1f5f9 !important',
          marginBottom: '1.5rem',
        },
        '.mypage_wrap .my_contbox .notebox p': {
          margin: 0,
        },
        '.mypage_wrap .my_contbox .notebox.flex': {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        '.mypage_wrap .my_contbox .notebox .tit': {
          fontWeight: 600,
          fontSize: '1rem',
          lineHeight: '100%',
          textTransform: 'uppercase',
          color: '#000',
        },
        '.mypage_wrap .my_contbox .notebox .descbox': {
          display: 'flex',
          flexDirection: 'column',
          gap: '0.813rem',
        },
        '.mypage_wrap .my_contbox .notebox .descitem': {
          display: 'flex',
          flexDirection: 'column',
          gap: '0.313rem',
        },
        '.mypage_wrap .my_contbox .notebox .desc': {
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: '143%',
          textTransform: 'uppercase',
          color: '#000',
        },
        '.mypage_wrap .my_contbox .notebox .desc .bold': {
          fontWeight: 600,
        },
        '.mypage_wrap .my_contbox .tbl_top .title': {
          fontWeight: 600,
          fontSize: '1.125rem',
          paddingLeft: '0.5rem',
        },
        '.mypage_wrap .my_contbox .my_tbl': {
          fontSize: '1rem',
          width: '100%',
          borderCollapse: 'collapse',
        },
        '.mypage_wrap .my_contbox .my_tbl thead tr': {
          borderTop: '1px solid #acacc5',
          borderBottom: '1px solid #acacc5',
          background: '#f1f5f9',
        },
        '.mypage_wrap .my_contbox .my_tbl thead tr th': {
          color: '#000',
          border: 'none',
          padding: '1rem 0',
        },
        '.mypage_wrap .my_contbox .my_tbl tbody tr': {
          borderBottom: '1px solid #acacc5',
        },
        '.mypage_wrap .my_contbox .my_tbl tbody tr td': {
          lineHeight: '100%',
          border: 'none',
          padding: '1.125rem 1.25rem',
        },
        '.mypage_wrap .my_contbox .my_tbl thead tr th .select_box .select': {
          width: 'auto',
          height: 'auto',
          minWidth: '4rem',
          padding: 0,
          paddingRight: '1.75rem',
          border: 'none',
          backgroundPositionX: 'right',
          fontWeight: 600,
          fontSize: '1rem',
          lineHeight: '100%',
          color: '#000',
          backgroundColor: 'transparent',
        },
        '.mypage_wrap .my_contbox .my_tbl tbody tr td.left_align': {
          padding: '1.125rem 1.25rem',
          textAlign: 'left',
        },
        '.mypage_wrap .my_contbox .pagination': {
          marginTop: '2rem',
          marginBottom: '3.125rem',
        },
        '.mypage_wrap .my_contbox .prdtop': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.25rem',
        },
        '.mypage_wrap .my_contbox .prdtop .leftbox': {
          display: 'flex',
          alignItems: 'center',
        },
        '.mypage_wrap .my_contbox .prdtop .total': {
          fontWeight: 600,
        },
        '.mypage_wrap .my_contbox .prdtop .rightbox': {
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
        },
        '.mypage_wrap .my_contbox .prdlist': {
          gap: 0,
          display: 'flex',
          flexDirection: 'column',
        },
        '.mypage_wrap .my_contbox .prdlist .prditem': {
          borderBottom: '1px solid #acacc5',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: '1.75rem',
          padding: '1.563rem 0',
        },
        '.mypage_wrap .my_contbox .prdlist .prditem:nth-child(1)': {
          borderTop: '1px solid #acacc5',
        },
        '.mypage_wrap .my_contbox .prdlist .prditem > .prdbox': {
          flex: 1,
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1.375rem',
        },
        '.mypage_wrap .my_contbox .prdlist .prditem > .prdbox .imgbox': {
          width: '8.125rem',
        },
        '.mypage_wrap .my_contbox .prdlist .prditem > .prdbox .txtbox': {
          paddingTop: '0.375rem',
        },
        '.mypage_wrap .my_contbox .prdlist .prditem > .prdbox .txtbox .badgebox': {
          marginBottom: '0.625rem',
        },
        '.mypage_wrap .my_contbox .prdlist .prditem > .prdbox .txtbox .title': {
          marginBottom: '0.875rem',
          fontSize: '1.375rem',
          fontWeight: 600,
        },
        '.mypage_wrap .my_contbox .prdlist .prditem > .prdbox .txtbox .info': {
          fontSize: '1.125rem',
          lineHeight: '100%',
          color: '#000',
          marginBottom: '0.875rem',
        },
        '.mypage_wrap .my_contbox .prdlist .prditem > .prdbox .txtbox .info .price': {
          fontWeight: 600,
        },
        '.mypage_wrap .my_contbox .prdlist .prditem > .prdbox .txtbox .bottom': {
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
        },
        '.mypage_wrap .my_contbox .prdlist .prditem > .prdbox .txtbox .bottom .bar': {
          width: '1px',
          height: '1.125rem',
          background: '#acacc5',
        },
        '.mypage_wrap .my_contbox .prdlist .prditem > .prdbox .txtbox .bottom .btn': {
          fontWeight: 400,
          fontSize: '1.125rem',
          lineHeight: '100%',
          color: '#000',
        },
        '.mypage_wrap .my_bottom': {
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid #acacc5',
          padding: '1.563rem 0rem',
        },
        '.mypage_wrap .my_bottom .rightbox .button': {
          width: 'auto',
          height: '4.375rem',
          fontWeight: 600,
          fontSize: '1.125rem',
          padding: '0 0.875rem',
        },
        '.mypage_wrap .my_bottom .rightbox .button.typeB': {
          fontWeight: 400,
        },
        '.mypage_wrap .my_bottom .pricebox': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '3.125rem',
        },
        '.mypage_wrap .my_bottom .pricebox .total': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '1rem',
          textAlign: 'right',
          fontWeight: 400,
          fontSize: '1.25rem',
          lineHeight: '100%',
          color: '#000',
        },
        '.mypage_wrap .my_bottom .pricebox .total .total_price': {
          fontWeight: 600,
          fontSize: '1.25rem',
          lineHeight: '100%',
          color: '#000',
        },
      },
      media: {
        '@media screen and (max-width: 1080px)': {
          '.mypage_wrap .my_titbox': {
            padding: '2.4rem 0 4rem',
            gap: '1.2rem',
            flexDirection: 'column',
            alignItems: 'flex-start',
          },
          '.mypage_wrap .my_titbox .breadcrumb': {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '1.3rem',
            fontWeight: 500,
            color: '#000',
            marginBottom: '0.5rem',
          },
          '.mypage_wrap .my_titbox .breadcrumb .menu_btn': {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#019ca3',
            border: 0,
            background: 'transparent',
            padding: 0,
            fontWeight: 600,
            fontSize: 'inherit',
          },
          '.mypage_wrap .my_titbox .tit': {
            fontSize: '1.8rem',
          },
          '.mypage_wrap .my_titbox .desc': {
            fontSize: '1.2rem',
            lineHeight: '140%',
            wordBreak: 'keep-all',
          },
          '.mypage_wrap .button': {
            flexShrink: 0,
            minWidth: '4.75rem',
            whiteSpace: 'nowrap',
          },
          '.mypage_wrap .button.small_button': {
            minHeight: '2.125rem',
            padding: '0.5rem 0.75rem',
            fontSize: '0.875rem',
          },
          '.mypage_wrap .my_contbox .prdtop .rightbox': {
            flexWrap: 'wrap',
          },
        },
      },
    });

    return `${baseCss}${rechargeCss}${memberCss}${productCss}${sellerRefundCss}${sellerCss}${purchaseCss}`;
  }, [wrapClass]);

  return (
    <div className={wrapClass} style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {wrapClass.includes('mypage_wrap') ? <style>{myPageCommonCss}</style> : null}
      <div className="container" style={{ flex: 1 }}>
        <section className="section mypage_section" style={sectionStyle}>
          <div className="inner mypage_contbox" style={contentRowStyle}>
            <MyPageSidebar ref={menuRef} isOpen={isMenuOpen} />
            <div className="my_content" style={myContentStyle}>
              <div className="my_titbox" style={titleBoxStyle}>
                <div className="breadcrumb" style={breadcrumbStyle}>
                  <span className="menu">마이페이지</span>
                  <i className="ph ph-caret-right arrow"></i>
                  <button type="button" className="menu_btn">
                    <span className="txt">{pageMeta.menuTitle ?? pageMeta.title ?? ''}</span>
                    <i className="ph ph-caret-circle-down icon"></i>
                  </button>
                </div>
                <p className="tit" style={titleStyle}>
                  {pageMeta.title ?? ''}
                </p>
                {pageMeta.description ? (
                  <p className="desc" style={descStyle}>
                    {pageMeta.description}
                  </p>
                ) : null}
              </div>

              <div className="my_contbox" style={contBoxStyle}>
                <Outlet context={outletContext} />
              </div>

              {footer}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
